import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { toast } from "react-hot-toast";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";
import { fetchPendingFacebookPages, selectFacebookPage } from "../../../services/facebookApi";

const getToken = async () => getAccessToken() || (await getAuthToken());

/**
 * Shown after Facebook authorization when the user manages more than one Page.
 * `selection` is the server-side id of the parked Page list; the Page tokens
 * themselves never reach the browser.
 */
const FacebookPagePickerDialog = ({ selection, onClose, onConnected }) => {
  const [pages, setPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!selection) return undefined;
    let cancelled = false;
    setLoading(true);
    setSelectedPageId("");

    (async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error("Not authenticated.");
        const result = await fetchPendingFacebookPages(selection, token);
        if (!cancelled) setPages(result);
      } catch (err) {
        if (cancelled) return;
        toast.error(err.response?.data?.message || err.message || "Failed to load Facebook Pages.");
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // onClose is a fresh closure each render of the parent; only `selection` should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated.");
      await selectFacebookPage(selection, selectedPageId, token);
      toast.success("Facebook Page connected successfully.");
      onConnected();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to connect Facebook Page.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(selection)} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Choose a Facebook Page</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pick the Page to connect to this workspace.
            </Typography>
            <RadioGroup value={selectedPageId} onChange={(e) => setSelectedPageId(e.target.value)}>
              {pages.map((page) => (
                <FormControlLabel key={page.id} value={page.id} control={<Radio />} label={page.name || page.id} />
              ))}
            </RadioGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedPageId || submitting}
          sx={{ textTransform: "none" }}
        >
          {submitting ? "Connecting…" : "Connect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacebookPagePickerDialog;
